import { Theme, createTheme } from "@mui/material/styles";

/**
 * Compose "simple / accessible mode" onto an already-resolved theme (light/dark, any brand).
 * Because it merges via `createTheme(base, overrides)`, it stacks on top of dark mode and the
 * brand palette without forking any of them — the only levers are typography scale, spacing,
 * touch-target minimums and contrast.
 *
 * Deliberately NOT changed: `theme.spacing()`'s base multiplier — every `sx={{ p: 2 }}` in the
 * app derives from it, so bumping it would cascade unpredictably. Density is increased through
 * component paddings and the larger type instead.
 *
 * Applied only to the patient audience (see AppEntry), so clinical/external UIs are untouched.
 */
export function withSimpleMode(base: Theme): Theme {
  const MIN_TARGET = 56; // px — comfortable target for older / low-dexterity users

  return createTheme(base, {
    typography: {
      // One lever: every rem-derived variant scales from fontSize. 14 (MUI default) -> 17.
      fontSize: 17,
      body1: { lineHeight: 1.7 },
      body2: { lineHeight: 1.65 },
      subtitle1: { lineHeight: 1.6 },
      subtitle2: { lineHeight: 1.6 },
    },
    components: {
      MuiButton: {
        defaultProps: { size: "large" },
        styleOverrides: {
          root: { minHeight: MIN_TARGET, paddingTop: 10, paddingBottom: 10 },
        },
      },
      MuiListItemButton: {
        styleOverrides: { root: { minHeight: MIN_TARGET } },
      },
      MuiIconButton: {
        styleOverrides: { root: { minWidth: 48, minHeight: 48 } },
      },
      MuiMenuItem: {
        styleOverrides: { root: { minHeight: MIN_TARGET } },
      },
      // Higher-contrast, thicker separators; larger surface padding for reduced density.
      MuiDivider: {
        styleOverrides: {
          root: { borderColor: base.palette.text.secondary },
        },
      },
      MuiCardContent: {
        styleOverrides: { root: { padding: 24, "&:last-child": { paddingBottom: 24 } } },
      },
      MuiListItem: {
        styleOverrides: { root: { paddingTop: 8, paddingBottom: 8 } },
      },
    },
  });
}
